import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Modal } from '@mui/material';
import useControlled from '@mui/utils/useControlled';
import { forwardRef, MouseEvent, ReactNode, useImperativeHandle } from 'react';
import css from './BaseModal.module.scss';

export type BaseModalRef = {
  open: () => void;
  close: () => void;
};

type BaseModalProps = {
  title?: string;
  subtitle?: string;
  headerContent?: ReactNode;
  onClose?: () => void;
  footerContent?: ReactNode;
  className?: string;
  children: ReactNode;
} & (
  | {
      /** Controlled visibility. The caller opens the modal itself, so there is no `onOpen`. */
      open: boolean;
      onOpen?: never;
    }
  | {
      /** Omitted: the modal is driven through the ref, and `onOpen` fires on `ref.open()`. */
      open?: undefined;
      onOpen?: () => void;
    }
);

export const BaseModal = forwardRef<BaseModalRef, BaseModalProps>(
  (
    {
      open: openProp,
      title,
      subtitle,
      headerContent,
      onClose,
      onOpen,
      footerContent,
      className,
      children,
    },
    ref,
  ) => {
    const [open, setOpen] = useControlled({
      controlled: openProp,
      default: false,
      name: 'BaseModal',
    });

    const handleOpen = () => {
      if (openProp !== undefined) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[BaseModal] ref.open() does nothing while `open` is controlled');
        }
        return;
      }
      if (open) return;
      setOpen(true);
      onOpen?.();
    };

    const handleClose = (event?: any, reason?: string) => {
      if (event) {
        event.stopPropagation();
      }

      setOpen(false);
      onClose?.();
    };

    const handleContentClick = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    };

    useImperativeHandle(ref, () => ({
      open: handleOpen,
      close: handleClose,
    }));

    return (
      <Modal
        open={open}
        onClose={handleClose}
        keepMounted={false}
        slotProps={{
          backdrop: {
            className: css.backdrop,
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <div className={`${css.baseModal} ${className || ''}`} onClick={handleContentClick}>
          <div className={css.header}>
            {headerContent || (
              <div className={css.titleAndSubtitle}>
                <span className={css.title}>{title}</span>
                {subtitle && <span className={css.subtitle}>{subtitle}</span>}
              </div>
            )}
            <IconButton
              onClick={handleClose}
              size="small"
              aria-label="Close"
              sx={{ marginLeft: 'auto' }}
            >
              <CloseIcon />
            </IconButton>
          </div>
          {children}
          {footerContent && <div className={css.footer}>{footerContent}</div>}
        </div>
      </Modal>
    );
  },
);
